// src/components/WhyToBuyUs.jsx
// Strona główna. Importowana NIE-leniwie (patrz komentarz w src/App.js),
// bo to ona jest elementem LCP i nie może mrugnąć przy hydratacji.
import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../css/modules/WhyToBuyUs.module.css';
import JsonLd from '../seo/JsonLd';
import Faq from './Faq';
import {
  HOME_FAQ,
  LICENCJA_PAGES,
  faqJsonLd,
  courseJsonLd,
  productJsonLd,
  getPlans,
  getLowestPrice,
  getFeaturedPlanId,
} from '../seo/siteMeta';
import { LICENCJA_PO_SLUGU, PROG_PROCENT, arkuszPrzedmiotu, przedmiotyLicencji } from '../data/licencje';
import { trackEvent } from '../lib/analytics';

const KROKI = [
  {
    tytul: 'Zacznij od darmowego pojedynku',
    opis: 'Wybierz uprawnienie, stań naprzeciw przeciwnika i odpowiedz na 10 pytań przekrojowych. Poprawna odpowiedź odbiera mu punkty życia, błędna kosztuje Twoją energię, a zegar tyka przy każdym pytaniu.',
  },
  {
    tytul: 'Nadrób słabe przedmioty',
    opis: 'W trybie nauki pytania idą przedmiotami, a poprawna odpowiedź pojawia się od razu razem z wyjaśnieniem i wskazaniem przepisu. Nie musisz szukać w rozporządzeniach.',
  },
  {
    tytul: 'Wracaj, aż wynik będzie stabilny',
    opis: 'Każdy pojedynek losuje nowy zestaw z całego materiału. Trenuj do skutku, bo na egzaminie liczy się nie tylko wiedza, ale też tempo odpowiadania.',
  },
];

const ATUTY = [
  {
    tytul: 'Test pod presją czasu',
    opis: 'Na egzaminie z prawa lotniczego wychodzi niecałe dwie minuty na pytanie. Nasz tryb pojedynku ćwiczy dokładnie to: odpowiadasz na czas, a błąd realnie kosztuje.',
  },
  {
    tytul: 'Wyjaśnienie przy każdym pytaniu',
    opis: 'Nie zostawiamy samej litery odpowiedzi. Przy każdym pytaniu piszemy, dlaczego ta odpowiedź jest poprawna i z jakiego przepisu to wynika.',
  },
  {
    tytul: 'Samoloty i drony w jednym miejscu',
    opis: 'PPL(A), LAPL(A), świadectwo kwalifikacji na ultralekkie oraz uprawnienia operatora drona A1/A3 i A2. Pytania wspólne dla kilku uprawnień przerabiasz raz.',
  },
  {
    tytul: 'Materiał zgodny ze stanem prawnym',
    opis: 'Pilnujemy zmian w przepisach. Przykład z tego roku: krajowe scenariusze NSTS przestały obowiązywać 31 grudnia 2025 roku i zastąpiły je europejskie STS-01 i STS-02.',
  },
];

const WhyToBuyUs = () => {
  const plany = getPlans();
  const najtanszy = getLowestPrice();
  // Wyróżniony pakiet z siteMeta - ta sama plakietka co w cenniku.
  const wyrozniony = getFeaturedPlanId();
  const ppl = LICENCJA_PO_SLUGU['ppl-a'];
  const przedmiotyPpl = przedmiotyLicencji(ppl);

  return (
    <div className={styles.page}>
      <JsonLd data={[faqJsonLd(HOME_FAQ), courseJsonLd(), productJsonLd()]} />

      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <p className={styles.eyebrow}>Egzaminy teoretyczne dla pilotów</p>
          <h1 className={styles.h1}>Zdaj teorię w ULC za pierwszym razem</h1>
          <p className={styles.lead}>
            Pytania do egzaminów na licencję PPL(A) i LAPL(A), świadectwo kwalifikacji na samoloty
            ultralekkie oraz uprawnienia operatora drona. Każde z wyjaśnieniem odpowiedzi
            i wskazaniem przepisu, a do tego tryb pojedynku, w którym liczy się także czas.
            Sprawdź się za darmo, bez zakładania konta.
          </p>

          <div className={styles.ctaRow}>
            <Link
              className={styles.primaryButton}
              to="/intro"
              onClick={() => trackEvent('cta_darmowy_test', { zrodlo: 'hero' })}
            >
              Rozegraj darmowy test
            </Link>
            <Link className={styles.secondaryButton} to="/cennik">
              Cennik od {najtanszy} zł
            </Link>
          </div>

          <p className={styles.accessInfo}>
            Demo bez rejestracji i bez płatności. Pełny dostęp kupujesz raz, bez subskrypcji.
          </p>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Wybierz swoje uprawnienie</h2>
        <p className={styles.sectionLead}>
          Każde uprawnienie ma własny zakres materiału i własne zasady zaliczania egzaminu.
        </p>
        <div className={styles.featureGrid}>
          {LICENCJA_PAGES.map(l => (
            <Link key={l.path} to={l.path} className={styles.featureCard}>
              <span className={styles.featureIcon}>{l.krotko}</span>
              <h3>{l.pelna}</h3>
              <p>{l.uprawnia}</p>
              <span className={styles.arrowButton}>Pytania i zakres egzaminu</span>
            </Link>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Jak wygląda nauka</h2>
        <ol className={styles.steps}>
          {KROKI.map((k, i) => (
            <li key={k.tytul} className={styles.step}>
              <span className={styles.stepNumber}>{i + 1}</span>
              <div>
                <h3>{k.tytul}</h3>
                <p>{k.opis}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Dlaczego u nas</h2>
        <div className={styles.featureGrid}>
          {ATUTY.map(a => (
            <div key={a.tytul} className={styles.featureCard}>
              <h3>{a.tytul}</h3>
              <p>{a.opis}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Dziewięć przedmiotów egzaminu PPL(A)</h2>
        <p className={styles.sectionLead}>
          Każdy przedmiot zdaje się osobno, przy progu {PROG_PROCENT} procent. Przy kaflu podajemy
          liczbę pytań i limit czasu obowiązujące na egzaminie. Kliknij, żeby przerobić przedmiot.
        </p>
        <div className={styles.featureGrid}>
          {przedmiotyPpl.map(p => {
            const arkusz = arkuszPrzedmiotu(ppl, p.slug);
            return (
              <Link key={p.slug} to={`/nauka/ppl-a/${p.slug}`} className={styles.featureCard}>
                <span className={styles.featureIcon}>{arkusz ? `${arkusz.pytan} pytań` : 'przedmiot'}</span>
                <h3>{p.nazwa}</h3>
                <p>{p.opis}</p>
              </Link>
            );
          })}
        </div>
        <div className={styles.ctaRow}>
          <Link className={styles.secondaryButton} to="/egzamin-teoretyczny-ulc">
            Jak wygląda egzamin teoretyczny w ULC
          </Link>
        </div>
      </section>

      {plany.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.h2}>Dostęp do pełnej bazy</h2>
          <p className={styles.sectionLead}>
            Płatność jednorazowa, bez subskrypcji i bez automatycznego przedłużania.
          </p>
          <div className={styles.planGrid}>
            {/* Wyróżniona karta MUSI nieść też klasę bazową: .planCardFeatured
                dokłada tylko obramowanie i cień, a `position: relative` siedzi
                w .planCard. Bez tego plakietka "najczęściej wybierany", która
                jest pozycjonowana absolutnie, uciekała na sam róg strony. */}
            {plany.map(p => (
              <div
                key={p.id}
                className={
                  p.id === wyrozniony
                    ? `${styles.planCard} ${styles.planCardFeatured}`
                    : styles.planCard
                }
              >
                {p.id === wyrozniony && (
                  <span className={styles.planBadge}>Najczęściej wybierany</span>
                )}
                <p className={styles.planPrice}>{p.price} zł</p>
                <p className={styles.planDesc}>
                  Dostęp na {p.days} {p.days === 1 ? 'dzień' : 'dni'}
                </p>
                <p className={styles.planDesc}>{p.desc}</p>
                <Link className={styles.primaryButton} to="/cennik">
                  Wybierz pakiet
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.h2}>Najczęstsze pytania</h2>
        <Faq items={HOME_FAQ} />
      </section>

      <section className={styles.section}>
        <div className={styles.prose}>
          <h2 className={styles.h2}>Skąd bierzemy pytania</h2>
          <p>
            Pytania są autorskie. Krajowy Bank Pytań Urzędu Lotnictwa Cywilnego nie jest
            udostępniany publicznie, więc nikt uczciwie nie oferuje jego kopii. Nasze pytania
            napisaliśmy pod program egzaminacyjny wynikający z przepisów Part-FCL, SERA oraz
            rozporządzeń 2019/945 i 2019/947 o bezzałogowych statkach powietrznych.
          </p>
          <p>
            Przy każdej odpowiedzi znajdziesz wyjaśnienie i wskazanie przepisu, z którego wynika.
            Serwis ma charakter edukacyjny i nie jest oficjalną stroną Urzędu Lotnictwa Cywilnego -
            nie wydajemy licencji i nie przeprowadzamy egzaminów.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WhyToBuyUs;
